// BL-2466 (PRJ-93 M1) — the one format for an @mention inside an internal note.
// A mention is stored as `@[staff:<uuid>]` in the plain-text body. These pure helpers
// (ADR 010 — Isomorphic Services) are shared by the note editor, the read-only note
// renderer, the server's mention check and, later, the notification renderer.

import type { InternalNoteEntityType } from '../../schemas/responses/internal-note';
// BL-2481 — kept out of the registry file so the API-surface generators' import
// closure doesn't grow by the role-grant graph.
import { NOTE_READ } from '../../api/registry/internal_notes';
import { rolesHoldGrantDeclaration } from '../auth/role-grants';
import type { Role } from '../auth/roles';

export { MENTION_CHIP_CLASS, OWN_MENTION_CHIP_CLASS, mentionChipClass } from './mention-chip';

/** One run of a note body: plain text, or a mention of one staff member. */
export type NoteSegment = { kind: 'text'; text: string } | { kind: 'mention'; staffUserId: string };

/**
 * The stored token. Exact on purpose: lower-case UUID only, so an email address,
 * a lone "@" or a hand-typed look-alike is never read as a mention (RAC 2).
 */
const MENTION_TOKEN = /@\[staff:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]/g;

/**
 * Write the token for one staff member.
 *
 * @param staffUserId - The mentioned `staff_users.id`.
 * @returns `@[staff:<uuid>]`.
 */
export function formatMentionToken(staffUserId: string): string {
  return `@[staff:${staffUserId}]`;
}

/**
 * Split a stored body into text and mention runs, in order. Empty text runs are
 * dropped, so a body that is only a token is one mention segment.
 *
 * @param body - The stored note text.
 * @returns The body's segments.
 */
export function parseNoteBody(body: string): NoteSegment[] {
  const segments: NoteSegment[] = [];
  let cursor = 0;
  for (const match of body.matchAll(MENTION_TOKEN)) {
    const start = match.index ?? 0;
    if (start > cursor) segments.push({ kind: 'text', text: body.slice(cursor, start) });
    segments.push({ kind: 'mention', staffUserId: match[1] });
    cursor = start + match[0].length;
  }
  if (cursor < body.length) segments.push({ kind: 'text', text: body.slice(cursor) });
  return segments;
}

/**
 * The staff members a body mentions, each once, in first-seen order (AC 6).
 *
 * @param body - The stored note text.
 * @returns Unique mentioned staff user ids.
 */
export function mentionedStaffIds(body: string): string[] {
  const ids = new Set<string>();
  for (const segment of parseNoteBody(body)) {
    if (segment.kind === 'mention') ids.add(segment.staffUserId);
  }
  return [...ids];
}

/** An inline node of the notes editor's document (plain JSON, no editor import). */
export type NoteEditorInline =
  | { type: 'text'; text: string }
  | { type: 'hardBreak' }
  | { type: 'mention'; attrs: { id: string; label: string } };

/** A paragraph of the notes editor's document. */
export interface NoteEditorParagraph {
  type: 'paragraph';
  content?: NoteEditorInline[];
}

/** The notes editor's document, as Tiptap's `getJSON()` returns it for its schema. */
export interface NoteEditorDoc {
  type: 'doc';
  content?: NoteEditorParagraph[];
}

/** One inline node as stored text: a token, a newline, or the text itself. */
function inlineToText(node: NoteEditorInline): string {
  if (node.type === 'mention') return formatMentionToken(node.attrs.id);
  if (node.type === 'hardBreak') return '\n';
  return node.text;
}

/**
 * Serialize the editor's document to the stored body. A chip becomes its id token,
 * a hard break and a paragraph boundary (pasted text) each become a newline.
 *
 * @param doc - The editor document.
 * @returns The body to store.
 */
export function editorDocToNoteBody(doc: NoteEditorDoc): string {
  return (doc.content ?? [])
    .map((paragraph) => (paragraph.content ?? []).map(inlineToText).join(''))
    .join('\n');
}

/**
 * Rebuild the editor's document from a stored body: one paragraph, newlines as hard
 * breaks, each token as a chip labelled with the staff member's current name.
 *
 * @param body - The stored note text.
 * @param labelFor - The current display name for a mentioned staff user id.
 * @returns The editor document. {@link editorDocToNoteBody} returns `body` unchanged.
 */
export function noteBodyToEditorDoc(body: string, labelFor: (staffUserId: string) => string): NoteEditorDoc {
  const content: NoteEditorInline[] = [];
  for (const segment of parseNoteBody(body)) {
    if (segment.kind === 'mention') {
      content.push({ type: 'mention', attrs: { id: segment.staffUserId, label: labelFor(segment.staffUserId) } });
      continue;
    }
    segment.text.split('\n').forEach((line, index) => {
      if (index > 0) content.push({ type: 'hardBreak' });
      if (line) content.push({ type: 'text', text: line });
    });
  }
  return { type: 'doc', content: [content.length > 0 ? { type: 'paragraph', content } : { type: 'paragraph' }] };
}

/** The inline refusal for a note that mentions someone who may not be mentioned (AC 8). */
export const MENTION_NOT_ALLOWED_MESSAGE = 'You can only mention teammates who can open this record.';

/** Why a teammate can't be mentioned on a note of this kind (D5): they can't open it. */
export const MENTION_UNAVAILABLE_REASON: Record<InternalNoteEntityType, string> = {
  lot: "Can't open lots",
  auction: "Can't open auctions",
  customer: "Can't open customers",
  invoice: "Can't open invoices",
  settlement: "Can't open settlements",
};

/**
 * Can someone holding these roles open a note's record? The one rule behind the
 * @ list, the save's mention check and the drain's send-time re-check (BL-2481),
 * so the three can't drift. Fails closed on an entity type with no grant.
 *
 * @param roles - The person's live roles in the note's house.
 * @param entityType - The kind of record the note sits on.
 * @returns `true` when the roles hold that record kind's {@link NOTE_READ} grant.
 */
export function rolesCanOpenNote(roles: readonly Role[], entityType: string): boolean {
  const grant = NOTE_READ[entityType as keyof typeof NOTE_READ];
  return grant != null && rolesHoldGrantDeclaration(roles, grant);
}
